import { useSyncExternalStore } from "react";
import { obterClienteDados } from "@/lib/obter-dados";

export type IconeCache = {
  nome: string;
  url: string | null;
  svg: string | null;
};

const CHAVE_CACHE = "icones-cache-v1";
let icones = new Map<string, IconeCache>();
const iconesServidor = new Map<string, IconeCache>();
let carregamento: Promise<void> | null = null;
const ouvintes = new Set<() => void>();

function notificar() {
  for (const ouvinte of ouvintes) ouvinte();
}

function normalizar(valor: unknown): IconeCache | null {
  if (!valor || typeof valor !== "object") return null;
  const registro = valor as Record<string, unknown>;
  const nome = String(registro.nome ?? "").trim();
  if (!nome) return null;
  return {
    nome,
    url: registro.url == null ? null : String(registro.url).trim() || null,
    svg: registro.svg == null ? null : String(registro.svg).trim() || null,
  };
}

function aplicar(dados: unknown) {
  const lista = Array.isArray(dados) ? dados : [];
  icones = new Map(
    lista
      .map(normalizar)
      .filter((icone): icone is IconeCache => !!icone)
      .map((icone) => [icone.nome, icone]),
  );
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(CHAVE_CACHE, JSON.stringify([...icones.values()]));
    } catch {
      /* mantém o cache em memória */
    }
  }
  notificar();
}

function carregarLocal() {
  if (typeof window === "undefined") return;
  try {
    const texto = window.localStorage.getItem(CHAVE_CACHE);
    if (texto) aplicar(JSON.parse(texto));
  } catch {
    /* cache local inválido: será substituído pela consulta */
  }
}

carregarLocal();

export function carregarIconesCache(): Promise<void> {
  if (carregamento) return carregamento;
  carregamento = (async () => {
    try {
      const banco = await obterClienteDados();
      const { data, error } = await (banco as any).from("icones").select("nome, url, svg");
      if (error) throw error;
      aplicar(data);
    } catch (erro) {
      console.warn("Falha ao carregar cache de ícones", erro);
    } finally {
      carregamento = null;
    }
  })();
  return carregamento;
}

export function obterIconeCache(nome: string) {
  return icones.get(nome.trim());
}

export function useCacheIcones() {
  return useSyncExternalStore(
    (ouvinte) => {
      ouvintes.add(ouvinte);
      return () => ouvintes.delete(ouvinte);
    },
    () => icones,
    () => iconesServidor,
  );
}

export function IconeReferenciado({ nome, className }: { nome: string; className?: string }) {
  const icone = useCacheIcones().get(nome.trim());
  if (!icone) return null;
  if (icone.svg) {
    return (
      <span
        className={className}
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: icone.svg }}
      />
    );
  }
  if (icone.url) return <img src={icone.url} alt="" className={className} aria-hidden="true" />;
  return null;
}